import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TimeslotStatusEnum } from '@sport-activities/sdk';
import { differenceInSeconds } from 'date-fns';
import { parse, toSeconds } from 'iso8601-duration';
import { Subscription as RxjsSubscription } from 'rxjs';
import { mergeMap, tap } from 'rxjs/operators';

import { SUBSCRIPTION_LOADER_KEY } from '~/attendee/components/shared/booking-detail/booking-detail.component';
import { BookingTypeEnum } from '~/attendee/enums/booking-type.enum';
import { Subscription } from '~/attendee/model/subscription.model';
import { SubscriptionService } from '~/attendee/service/subscription.service';
import { TipsVariantEnum } from '~/component/tips/enum/variant.enum';
import { getRefundTimeLimit } from '~/core/helpers/refund-time-limit/refund-time-limit.helper';
import { LoaderService } from '~/core/services/loader/loader.service';
import { defaultRefundTimeLimit } from '~/toggles/default-values/global/default-refund-time-limit';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';
import { VtmnPlayButtonVariantEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-variant.enum';
import { VtmnPlayIconTypeEnum } from '~/component/vtmn-play-icon/enums/vtmn-play-icon-type.enum';
import { VtmnPlayButtonShapeEnum } from '~/component/buttons/vtmn-play-button/enum/vtmn-play-button-shape.enum';
import { ViewportScroller } from '@angular/common';

@Component({
  selector: 'app-attendee-bookings-list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
})
export class AttendeeBookingsListComponent implements OnInit, OnDestroy {
  private readonly subscription = new RxjsSubscription();

  public readonly icons = VtmnPlayIconTypeEnum;
  public readonly tipsVariant = TipsVariantEnum;
  public readonly vtmnPlayButtonVariant = VtmnPlayButtonVariantEnum;
  public readonly vtmnPlayButtonShape = VtmnPlayButtonShapeEnum;
  public readonly subscriptionLoaderKey = 'SUBSCRIPTION_LOADER_KEY';
  public isLoading = false;
  public hasSubscriptionError = false;
  public listType: BookingTypeEnum;
  public subscriptions: Subscription[]; // undefinied will show the loader
  public p = 1;

  constructor(
    private readonly router: Router,
    private readonly localizeRouterService: LocalizeRouterService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly subscriptionService: SubscriptionService,
    private readonly toggleService: ToggleService,
    private readonly loaderService: LoaderService,
    private readonly viewportScroller: ViewportScroller
  ) {}

  public ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public ngOnInit(): void {
    this.subscription.add(
      this.loaderService.loading$.subscribe(() => {
        this.isLoading = this.loaderService.isLoading(
          this.subscriptionLoaderKey
        );
      })
    );

    this.loaderService.start(this.subscriptionLoaderKey);

    this.subscription.add(
      this.activatedRoute.data
        .pipe(
          tap(() => this.loaderService.start(SUBSCRIPTION_LOADER_KEY)),
          mergeMap((data) => {
            this.listType = data.type;
            return this.subscriptionService.getSubscriptions(this.listType);
          })
        )
        .subscribe({
          next: (subscriptions) => {
            // empty array will hide loader and display the no-results
            this.loaderService.stop(this.subscriptionLoaderKey);
            this.subscriptions = subscriptions || [];
          },
          error: () => {
            this.hasSubscriptionError = true;
            this.loaderService.stop(this.subscriptionLoaderKey);
          },
        })
    );
  }

  public get isPastActivityTab(): boolean {
    return this.router.url.includes(
      this.localizeRouterService.translateRoute('attendee.past').toString()
    );
  }

  public canRefund(subscription: Subscription): boolean {
    const { timeslot, activity } = subscription;

    // session happens too soon to cancel
    const refundAllowedDelay = toSeconds(
      parse(
        getRefundTimeLimit(
          activity,
          this.toggleService.getVariantObject(
            ToggleNameEnum.RefundTimeLimit,
            defaultRefundTimeLimit
          )
        )
      )
    );

    const isCancelableByDate =
      differenceInSeconds(new Date(timeslot.startDate), new Date()) >
      refundAllowedDelay;

    // timeslot is cancelled
    const isCancelableByStatus =
      timeslot.status === ('PUBLISHED' as TimeslotStatusEnum);

    return (
      isCancelableByDate &&
      isCancelableByStatus &&
      this.listType === BookingTypeEnum.UPCOMING
    );
  }

  public changePage($event): void {
    this.p = $event;
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  // remove from list after refund
  public subscriptionCancelled(subscription: Subscription): void {
    this.subscriptions = this.subscriptions.filter((s) => s !== subscription);
  }
}
